import { ThemeToggle } from "@/components/ThemeToggle";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Shield, Lock, Eye, Database, Mail, UserCheck, Globe, Clock, AlertTriangle, ArrowLeft } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {AutoTranslate} from "@/lib/autoTranslate.tsx"
import { SEO } from "@/components/SEO";


export default function Privacy() {
  const lastUpdated = AutoTranslate(`September 19th, 2025`);
  const navigate = useNavigate();

  const handleBackToLanding = () => {
    navigate('/');
  };

  const sections = [
    {
      icon: <Database className="h-5 w-5 text-primary" />,
      title: AutoTranslate(`1. Information We Collect`),
      content: AutoTranslate(`We collect information you provide directly to us when using our AI-powered resume building service:

• Personal Information: Full name, email address, phone number, location (city and country)
• Professional Information: Work experience, education, skills, certifications, job titles, employment dates
• Account Data: Login credentials, subscription information, preferences
• Usage Data: How you interact with our service, pages visited, features used
• Communication Records: Messages sent through our support system or contact forms
• Payment Information: Processed securely through Paystack (we do not store payment card or bank details)
• Audio Data: Voice recordings when using our voice input feature (processed and deleted immediately after transcription)`)
    },
    {
      icon: <Eye className="h-5 w-5 text-primary" />,
      title: AutoTranslate(`2. How We Use Your Information`),
      content: AutoTranslate(`We use your information solely to provide and improve our resume building service:

• Resume Creation: Generate professional resumes based on your provided information
• Account Management: Maintain your account, process subscriptions, and provide customer support
• Service Improvement: Analyze usage patterns to enhance our AI algorithms and user experience
• Communication: Send important updates, respond to inquiries, and provide customer support
• Security: Protect against fraud, unauthorized access, and ensure service integrity
• Legal Compliance: Meet legal obligations and protect our rights and interests
• Analytics: Understand how users interact with our service to make improvements`)
    },
    {
      icon: <UserCheck className="h-5 w-5 text-primary" />,
      title: AutoTranslate(`3. Information Sharing and Disclosure`),
      content: AutoTranslate(`We DO NOT sell, trade, or rent your personal information to third parties. We may share information only in these limited circumstances:

• Service Providers: Trusted partners who help us operate our service (Supabase for data storage, Paystack for payments, OpenAI for AI processing)
• Legal Requirements: When required by law, court order, or to protect our rights and safety
• Business Transfers: In the event of a merger, acquisition, or sale of assets
• Consent: When you explicitly authorize us to share your information
• Anonymized Data: We may share aggregated, anonymized data that cannot identify individual users`)
    },
    {
      icon: <Lock className="h-5 w-5 text-primary" />,
      title: AutoTranslate(`4. Data Security and Protection`),
      content: AutoTranslate(`We implement robust security measures to protect your information:

• Encryption: All data is encrypted in transit and at rest using industry-standard protocols
• Access Controls: Strict access controls limit who can access your personal information
• Regular Security Audits: We conduct regular security assessments and updates
• Secure Infrastructure: Our service is hosted on secure, compliant cloud infrastructure
• Data Minimization: We collect only the information necessary to provide our service
• Secure Authentication: Multi-factor authentication and secure login protocols
• Incident Response: Procedures in place to respond quickly to any security incidents`)
    },
    {
      icon: <Shield className="h-5 w-5 text-primary" />,
      title: AutoTranslate(`5. GDPR Compliance & Your Data Rights`),
      content: AutoTranslate(`We are committed to complying with the General Data Protection Regulation (GDPR) and other applicable data protection laws. Your resume information is safe, private, and handled with the utmost care:

• Lawful Basis: We process your data based on your consent and contractual necessity to provide our resume building service
• Data Controller: We act as the data controller for your personal information
• Your Resume Data is Private: Your resume content is never shared, sold, or used for any purpose other than providing you with our service
• Right to Access: You can request a copy of all personal data we hold about you at any time
• Right to Rectification: You can correct any inaccurate or incomplete personal data
• Right to Erasure: You can request complete deletion of your data ("right to be forgotten")
• Right to Restrict Processing: You can limit how we process your personal data
• Right to Data Portability: You can receive your data in a machine-readable format
• Right to Object: You can object to processing of your personal data
• Right to Withdraw Consent: You can withdraw consent for data processing at any time
• Right to Lodge a Complaint: You have the right to file a complaint with your local Data Protection Authority if you believe your rights have been violated
• Response Time: We will respond to all data rights requests within 30 days
• No Discrimination: Exercising your rights will not result in any discriminatory treatment

If you feel your data privacy rights have been violated or you have concerns about how we handle your information, you are entitled to take lawful measures, including filing a complaint with your local Data Protection Authority.`)
    },
    {
      icon: <Globe className="h-5 w-5 text-primary" />,
      title: AutoTranslate(`6. International Data Transfers`),
      content: AutoTranslate(`Your information may be processed and stored in countries other than your own:

• Data Processing: Our service providers may process data in the United States and other countries
• Safeguards: We ensure appropriate safeguards are in place for international transfers, including Standard Contractual Clauses (SCCs) where required
• Compliance: We comply with applicable data protection laws in all jurisdictions, including GDPR transfer requirements
• Your Rights: Your data protection rights remain in effect regardless of processing location
• Transparency: We will inform you of any changes to where your data is processed`)
    },
    {
      icon: <Clock className="h-5 w-5 text-primary" />,
      title: AutoTranslate(`7. Data Retention`),
      content: AutoTranslate(`We retain your information only as long as necessary:

• Active Accounts: Information is retained while your account is active
• Inactive Accounts: Data may be retained for up to 3 years after account deactivation
• Legal Requirements: Some data may be retained longer to comply with legal obligations
• Deletion Requests: You can request immediate deletion of your account and data - we will process this within 30 days
• Backup Data: Backup copies are automatically deleted within 90 days of primary deletion
• Anonymous Analytics: Anonymized usage data may be retained indefinitely for service improvement`)
    },
    {
      icon: <Shield className="h-5 w-5 text-primary" />,
      title: AutoTranslate(`8. Your Privacy Rights`),
      content: AutoTranslate(`You have the following rights regarding your personal information:

• Access: Request a copy of all personal information we hold about you
• Correction: Update or correct inaccurate or incomplete information
• Deletion: Request deletion of your personal information (right to be forgotten)
• Portability: Receive your data in a machine-readable format
• Restriction: Limit how we process your personal information
• Objection: Object to certain types of processing
• Withdraw Consent: Withdraw consent for data processing at any time
• Complaint: File a complaint with relevant data protection authorities`)
    },
    {
      icon: <AlertTriangle className="h-5 w-5 text-primary" />,
      title: AutoTranslate(`9. Cookies and Tracking`),
      content: AutoTranslate(`We use cookies and similar technologies to enhance your experience:

• Essential Cookies: Required for basic site functionality and security
• Analytics Cookies: Help us understand how users interact with our service
• Preference Cookies: Remember your settings and preferences
• Third-Party Cookies: Some features may use cookies from trusted service providers
• Cookie Control: You can manage cookie preferences through your browser settings
• Do Not Track: We respect browser "Do Not Track" signals where technically feasible`)
    },
    {
      icon: <Mail className="h-5 w-5 text-primary" />,
      title: AutoTranslate(`10. Communication Preferences`),
      content: AutoTranslate(`You have control over the communications you receive from us:

• Service Communications: Important updates about your account and our service
• Marketing Communications: Promotional emails (you can opt out at any time)
• Support Communications: Responses to your inquiries and support requests
• Unsubscribe Options: Easy unsubscribe links in all marketing communications
• Preference Center: Manage your communication preferences in your account settings`)
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-main">
      <SEO
        title="Privacy Policy — FIND Resume Builder"
        description="Read how FIND Resume Builder collects, stores, and protects your personal data, resume content, and account information under GDPR-compliant practices."
        path="/privacy"
      />
      <div
        className="fixed inset-0 opacity-30 pointer-events-none dark:opacity-10"
        style={{
          backgroundImage: `var(--grid-pattern)`,
        }}
      />

      {/* Fixed Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToLanding}
                className="flex items-center text-foreground hover:text-primary"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                <span className="hidden md:inline text-sm md:text-base">{AutoTranslate("Back")}</span>
              </Button>
              <div className="flex items-center space-x-2">
                <Shield className="h-5 w-5 md:h-6 md:w-6 text-primary" />
                <h1 className="text-base md:text-2xl font-bold text-foreground text-center md:text-left">{AutoTranslate(`Privacy Policy`)}</h1>
              </div>
            </div>
            <div className="hidden md:block">
              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>

      <div className="relative z-10 mx-auto px-0 pt-32">
        <div className="space-y-8 mb-8">
          {/* Header */}
          <div className="text-center space-y-4 px-4">
            <p className="text-muted-foreground">
              {AutoTranslate(`Your privacy is important to us. This policy explains how we collect, use, and protect your information.`)}
            </p>
            <p className="text-sm text-muted-foreground">
              {AutoTranslate(`Last updated: `)}{lastUpdated}
            </p>
          </div>

          {/* Introduction */}
          <Card className="max-w-4xl justify-center lg:mx-auto mx-4 bg-card/80 backdrop-blur-sm border-card-border">
            <CardContent className="p-6">
              <p className="text-muted-foreground leading-relaxed">
                {AutoTranslate(`Welcome to Resume Builder AI. We are committed to protecting your privacy and ensuring the security of your personal information.
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered resume building service.
                By using our service, you agree to the collection and use of information in accordance with this policy.`)}
              </p>
            </CardContent>
          </Card>

          {/* Privacy Sections */}
          {sections.map((section, index) => (
            <Card key={index} className="max-w-4xl justify-center lg:mx-auto mx-4 bg-card/80 backdrop-blur-sm border-card-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  {section.icon}
                  {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none text-muted-foreground">
                  {section.content.split('\n').map((paragraph, pIndex) => {
                    if (paragraph.trim().startsWith('•')) {
                      return (
                        <div key={pIndex} className="flex items-start gap-2 mb-2">
                          <span className="text-primary mt-1.5">•</span>
                          <span>{paragraph.trim().substring(1).trim()}</span>
                        </div>
                      );
                    } else if (paragraph.trim()) {
                      return (
                        <p key={pIndex} className="mb-3 leading-relaxed">
                          {paragraph.trim()}
                        </p>
                      );
                    }
                    return null;
                  })}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Refund Policy */}
          <Card className="max-w-4xl justify-center lg:mx-auto mx-4 bg-card/80 backdrop-blur-sm border-card-border border-l-4 border-l-primary">
            <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="h-5 w-5 text-primary" />
              11. {AutoTranslate(`Refund Policy`)}
            </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-muted-foreground">
                <p className="leading-relaxed">
                  {AutoTranslate(`Payments for Pro subscriptions are processed securely through Paystack. We offer a`)} <strong>{AutoTranslate(`14-day refund policy`)}</strong>{AutoTranslate(` for eligible subscription payments, provided the Pro benefits from that billing period have not been used:`)}
                </p>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="text-primary font-bold">1.</span>
                    <span>{AutoTranslate(`Eligibility: Only your most recent Pro subscription payment is eligible for review, and only if you have not used Pro benefits such as Pro downloads, saved resume storage, advanced templates, ATS/job tailoring, or other paid features after that payment`)}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-primary font-bold">2.</span>
                    <span>{AutoTranslate(`Time Limit: Refund requests must be submitted within 14 days of your most recent subscription payment`)}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-primary font-bold">3.</span>
                    <span>{AutoTranslate(`Usage Review: We may review account activity, payment confirmation, resume downloads, saved resumes, and Pro feature usage to confirm eligibility`)}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-primary font-bold">4.</span>
                    <span>{AutoTranslate(`Exclusions: Previous payments outside the 14-day window, partial billing periods, and payments where Pro benefits were already used are not eligible for refunds`)}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-primary font-bold">5.</span>
                    <span>{AutoTranslate(`Request Process: Submit refund requests to `)} <a href="mailto:info@wefindservices.org" className="text-primary underline">info@wefindservices.org</a>{AutoTranslate(` with your account email, payment date, Paystack reference if available, and a brief reason for the request`)}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-primary font-bold">6.</span>
                    <span>{AutoTranslate(`Refund Timing: Approved refunds are issued within 14 days to the original payment method used through Paystack, although your bank or card provider may take additional time to reflect the money`)}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-primary font-bold">7.</span>
                    <span>{AutoTranslate(`Account Access: Once a refund is issued, the account returns to the Free plan and Pro access is removed`)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="max-w-4xl justify-center lg:mx-auto mx-4 bg-card/80 backdrop-blur-sm border-card-border">
            <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Mail className="h-5 w-5 text-primary" />
              12. {AutoTranslate(`Contact Us`)}
            </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-muted-foreground">
                <p className="leading-relaxed">
                  {AutoTranslate(`If you have any questions about this Privacy Policy, your data rights, or our privacy practices, please contact us:`)}
                </p>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="font-medium text-foreground mb-2">{AutoTranslate(`Privacy Officer`)}</p>
                  <p className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-primary" />
                    <a href="mailto:info@wefindservices.org" className="text-primary underline">
                      info@wefindservices.org
                    </a>
                  </p>
                  <p className="text-sm mt-2">
                    {AutoTranslate(`We will respond to your inquiry within 48 hours during business days.`)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Updates Notice */}
          <Card className="max-w-4xl justify-center lg:mx-auto mx-4 bg-card/80 backdrop-blur-sm border-card-border">
            <CardContent className="p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 bg-card/80 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-base mb-1">{AutoTranslate(`Policy Updates`)}</p>
                  <p className="text-muted-foreground">
                    {AutoTranslate(`We may update this Privacy Policy from time to time. We will notify you of any significant changes
                    by email and by posting the new policy on this page with an updated revision date.`)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Footer />
      </div>
    </div>
  );
}
